/**
 * ════════════════════════════════════════════════════════════════
 * AdminImportV2 — Import V2 Industrialisé P5.3.2
 * ════════════════════════════════════════════════════════════════
 * P5.3.2: CSV/XLSX/JSON upload, templates, auto-detection, products import
 * Pipeline: Upload File → Auto-detect → Preview → Confirmation → Import → Résumé
 * ════════════════════════════════════════════════════════════════
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Upload, FileJson, AlertTriangle, CheckCircle, XCircle, Eye, Play, RotateCcw, FileText, Clock, ArrowRight, Shield, Info, Download, FileSpreadsheet, Table2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DashboardLayout from '../../components/DashboardLayout';
import api, { API } from '../../lib/api';
import { toast } from 'sonner';

const IMPORT_TYPES = [
  { value: 'products', label: 'Produits', description: 'Catalogue produit, EAN, fiches', icon: '📦' },
  { value: 'offers', label: 'Offres Marketing', description: 'Campagnes, promotions, visibility', icon: '🏷️' },
  { value: 'mechanics', label: 'Conditions d\'achat', description: 'Pricing, paliers, remises', icon: '⚙️' },
  { value: 'integrity', label: 'Intégrité Commerciale', description: 'Audit, migration, nettoyage', icon: '🛡️' },
  { value: 'history', label: 'Historique Imports', description: 'Journal des imports exécutés', icon: '📋' },
];

/* ═══ Normalizer Corrections Display ═══ */
function CorrectionsBadges({ corrections }) {
  if (!corrections?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {corrections.map((c, i) => (
        <Badge key={i} variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">
          {c}
        </Badge>
      ))}
    </div>
  );
}

/* ═══ Intra-Duplicate Warning Panel ═══ */
function IntraDuplicatesPanel({ duplicates }) {
  if (!duplicates?.length) return null;
  return (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="intra-duplicates-panel">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <p className="font-medium text-amber-800 text-sm">{duplicates.length} doublon(s) intra-fichier détecté(s)</p>
      </div>
      <div className="space-y-1">
        {duplicates.map((d, i) => (
          <p key={i} className="text-xs text-amber-700 font-mono">
            {d.offer_code ? `offer_code "${d.offer_code}"` : `clé "${d.key}"`} → lignes {d.rows.join(', ')}
          </p>
        ))}
      </div>
    </div>
  );
}

/* ═══ Product Preview Table — enriched with statuses + selection ═══ */
const ACTION_CONFIG = {
  create:           { label: 'Création',           color: 'bg-emerald-100 text-emerald-800', selectable: true },
  update:           { label: 'Mise à jour',        color: 'bg-blue-100 text-blue-800',       selectable: true },
  add_distributor:  { label: 'Ajout distributeur', color: 'bg-purple-100 text-purple-800',   selectable: true },
  blocked:          { label: 'Bloqué',             color: 'bg-red-100 text-red-800',         selectable: false },
  skip:             { label: 'Ignoré',             color: 'bg-gray-100 text-gray-600',       selectable: false },
};

function ProductPreviewTable({ rows, selectedRows, onToggleRow, onSelectAll, onDeselectAll }) {
  const importableCount = rows.filter(r => ACTION_CONFIG[r.action]?.selectable).length;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{selectedRows.size} / {importableCount} ligne(s) sélectionnée(s)</p>
        <div className="flex gap-2">
          <button onClick={onSelectAll} className="text-xs text-blue-600 hover:underline" data-testid="select-all-btn">Tout sélectionner</button>
          <button onClick={onDeselectAll} className="text-xs text-blue-600 hover:underline" data-testid="deselect-all-btn">Tout désélectionner</button>
        </div>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-[400px] overflow-auto">
          <table className="w-full text-sm" data-testid="product-preview-table">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-center w-8"></th>
                <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Ligne</th>
                <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">EAN</th>
                <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Produit</th>
                <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Distributeur</th>
                <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Action</th>
                <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Statut / Détail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r, idx) => {
                const cfg = ACTION_CONFIG[r.action] || ACTION_CONFIG.skip;
                const isSelected = selectedRows.has(r.row);
                const isBlocked = r.action === 'blocked';
                const hasWarnings = r.warnings?.length > 0;
                const hasDiffs = r.global_diffs?.length > 0;

                return (
                  <tr
                    key={idx}
                    className={`${isBlocked ? 'bg-red-50/60' : hasWarnings ? 'bg-amber-50/40' : isSelected ? 'bg-white' : 'bg-gray-50/30'} hover:bg-gray-50`}
                    data-testid={`preview-row-${r.row}`}
                  >
                    <td className="px-2 py-1.5 text-center">
                      {cfg.selectable ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleRow(r.row)}
                          className="rounded border-gray-300"
                          data-testid={`checkbox-row-${r.row}`}
                        />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs text-gray-500">{r.row + 1}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{r.code_ean || '—'}</td>
                    <td className="px-2 py-1.5 text-xs max-w-[180px] truncate font-medium" title={r.intitule}>{r.intitule || '—'}</td>
                    <td className="px-2 py-1.5 text-xs">
                      {r.distributor_name || r.distributeur || '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      {r.match_method && (
                        <span className="ml-1 text-[9px] text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-200">{r.match_method}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-xs space-y-0.5">
                      {isBlocked && <p className="text-red-600 font-medium">{r.reason}</p>}
                      {r.errors?.map((e, ei) => <p key={ei} className="text-red-500 text-[10px]">{e}</p>)}
                      {r.warnings?.map((w, wi) => <p key={wi} className="text-amber-600 text-[10px]">{w}</p>)}
                      {hasDiffs && (
                        <details className="mt-1">
                          <summary className="text-[10px] text-blue-600 cursor-pointer">Données globales différentes ({r.global_diffs.length})</summary>
                          <div className="mt-1 space-y-0.5 pl-2 border-l-2 border-blue-200">
                            {r.global_diffs.map((d, di) => (
                              <p key={di} className="text-[10px] text-gray-600">
                                <span className="font-medium">{d.field}</span>: <span className="text-gray-400 line-through">{d.current}</span> → <span className="text-blue-600">{d.imported}</span>
                                <span className="text-[9px] text-gray-400 ml-1">(non écrasé)</span>
                              </p>
                            ))}
                          </div>
                        </details>
                      )}
                      <CorrectionsBadges corrections={r.corrections} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══ Normalizer Stats Summary — P5.3.3 enriched ═══ */
function NormalizerStats({ stats }) {
  if (!stats) return null;
  return (
    <div className="space-y-2" data-testid="normalizer-stats">
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {[
          { label: 'Total', val: stats.total, color: 'text-gray-700' },
          { label: 'Valides', val: stats.valid, color: 'text-emerald-700' },
          { label: 'Erreurs', val: stats.with_errors, color: stats.with_errors ? 'text-red-700' : 'text-gray-500' },
          { label: 'Corrections', val: stats.with_corrections, color: stats.with_corrections ? 'text-blue-700' : 'text-gray-500' },
          { label: 'Doublons', val: stats.with_duplicates, color: stats.with_duplicates ? 'text-amber-700' : 'text-gray-500' },
        ].map((s, i) => (
          <div key={i} className="p-2 bg-white border rounded-md text-center">
            <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
            <p className="text-[10px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>
      {/* P5.3.3: Ignored columns info */}
      {stats.ignored_columns?.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ignored-columns-info">
          <Info className="h-3 w-3 flex-shrink-0" />
          <span>Colonnes ignorées : {stats.ignored_columns.join(', ')}</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * Resolution Helpers — Apply admin choices to import rows
 * ═══════════════════════════════════════════════════════════════ */

function extractQuotedValue(str) {
  if (!str) return null;
  const match = str.match(/[''"«]([^''"»]+)[''"»]/);
  return match ? match[1] : null;
}

/** Apply warning resolutions to raw parsed rows before sending to backend */
function applyResolutions(rows, resolutions) {
  if (!resolutions || !Object.keys(resolutions).length) return rows;
  const modified = rows.map(r => ({ ...r }));
  for (const [, res] of Object.entries(resolutions)) {
    const rowIdx = res.row;
    if (rowIdx == null || rowIdx >= modified.length) continue;
    const row = modified[rowIdx];

    if (res.field === 'editeur') {
      const editeurKey = Object.keys(row).find(k =>
        ['editeur', 'marque', 'brand', 'publisher'].includes(k.toLowerCase())
      ) || 'editeur';
      if (res.action === 'use_existing') {
        const current = (row[editeurKey] || '').trim();
        const original = (res.original || '').trim();
        // Exact match (case-insensitive): replace entirely
        if (current.toLowerCase() === original.toLowerCase()) {
          modified[rowIdx] = { ...row, [editeurKey]: res.value };
        } else if (original && current.toLowerCase().includes(original.toLowerCase())) {
          // Multi-editor (coedition): replace the specific part
          modified[rowIdx] = { ...row, [editeurKey]: current.replace(
            new RegExp(original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), res.value
          )};
        } else {
          modified[rowIdx] = { ...row, [editeurKey]: res.value };
        }
      }
      // create_new → keep original value, backend will create via sync_publishers
    } else if (res.field === 'editeur' && res.action === 'skip') {
      // "Ne pas associer" — keep original value in the row (backend still needs it for validation)
      // The resolution metadata tells the backend to skip sync_publishers
      // Do NOT delete the field — it would trigger MISSING_EDITEUR
    } else if (res.field === 'type_jeu') {
      const tjKey = Object.keys(row).find(k =>
        ['type_jeu', 'categorie', 'category', 'genre'].includes(k.toLowerCase())
      ) || 'type_jeu';
      if (res.action === 'use_suggestion' || res.action === 'select') {
        modified[rowIdx] = { ...row, [tjKey]: res.value };
      } else if (res.action === 'skip') {
        const copy = { ...row };
        delete copy[tjKey];
        modified[rowIdx] = copy;
      }
    } else if (res.field === 'public_cible') {
      const pcKey = Object.keys(row).find(k =>
        ['public_cible', 'public', 'audience'].includes(k.toLowerCase())
      ) || 'public_cible';
      if (res.action === 'use_suggestion') {
        modified[rowIdx] = { ...row, [pcKey]: res.value };
      } else if (res.action === 'skip') {
        const copy = { ...row };
        delete copy[pcKey];
        modified[rowIdx] = copy;
      }
    } else if (res.field === 'distributeur') {
      const distKey = Object.keys(row).find(k =>
        ['distributeur', 'distributor', 'fournisseur', 'partner', 'partenaire'].includes(k.toLowerCase())
      ) || 'distributeur';
      if (res.action === 'use_existing') {
        modified[rowIdx] = { ...row, [distKey]: res.value };
      }
    }
  }
  return modified;
}

/* ═══ Warning Group Container ═══ */
function WarningGroup({ title, color, children }) {
  const styles = {
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
  };
  const cls = styles[color] || styles.slate;
  return (
    <div className={`p-4 border rounded-lg space-y-2 ${cls.split(' ').slice(0, 2).join(' ')}`}>
      <p className={`font-medium text-sm ${cls.split(' ')[2]}`}>{title}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

/* ═══ Resolution Card ═══ */
function ResolutionCard({ row, label, resolved, testId, title, children }) {
  return (
    <div className={`p-3 bg-white rounded border transition-colors ${resolved ? 'border-emerald-300' : 'border-gray-200'}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <p className="text-xs">
            Ligne <span className="font-mono font-semibold">{row + 1}</span> :
            <span className="font-mono font-bold ml-1">{'\u00AB'} {label} {'\u00BB'}</span>
          </p>
          {title && (
            <p className="text-[10px] text-gray-500 truncate mt-0.5" title={title}>
              Produit : {title}
            </p>
          )}
        </div>
        {resolved && (
          <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200 flex-shrink-0 ml-2" variant="outline">
            <CheckCircle className="h-3 w-3 mr-1" /> Résolu
          </Badge>
        )}
      </div>
      {children}
    </div>
  );
}

/* ═══ Warning Resolution Panel — main resolution UI ═══ */
function WarningResolutionPanel({ analysisResult, resolutions, onResolve, partners, referentiels: refs, parsedRows }) {
  if (!analysisResult) return null;

  // Helper: get product title from parsed row
  const getRowTitle = (rowIdx) => {
    if (!parsedRows || rowIdx >= parsedRows.length) return null;
    const row = parsedRows[rowIdx];
    return row?.intitule || row?.titre || row?.title || row?.nom || row?.name || null;
  };

  const warnings = analysisResult.warnings || [];
  const errors = analysisResult.errors || [];

  const publisherCloseMatch = warnings.filter(w => w.warning_code === 'PUBLISHER_CLOSE_MATCH');
  const publisherNew = warnings.filter(w => w.warning_code === 'PUBLISHER_NEW');
  const typeJeuMismatch = warnings.filter(w => w.warning_code === 'REFERENTIEL_MISMATCH' && w.field === 'type_jeu');
  const publicCibleMismatch = warnings.filter(w => w.warning_code === 'REFERENTIEL_MISMATCH' && w.field === 'public_cible');
  const multiTypeJeu = warnings.filter(w => w.warning_code === 'MULTI_VALUE_TYPE_JEU');
  const tagResolutions = warnings.filter(w => w.warning_code === 'TAG_RESOLUTION');
  const distributorErrors = errors.filter(e =>
    e.error_code === 'UNKNOWN_DISTRIBUTOR' || (typeof e.error === 'string' && e.error.includes('UNKNOWN_DISTRIBUTOR'))
  );
  const distributorItems = distributorErrors.map(e => ({
    row: e.row,
    value: e.value || extractQuotedValue(e.error),
    suggestion: e.suggestion || null,
  }));

  const allKeys = [
    ...publisherCloseMatch.map(w => `PUBLISHER_CLOSE_MATCH_${w.row}_${w.value}`),
    ...publisherNew.map(w => `PUBLISHER_NEW_${w.row}_${w.value}`),
    ...typeJeuMismatch.map(w => `REFERENTIEL_MISMATCH_type_jeu_${w.row}`),
    ...publicCibleMismatch.map(w => `REFERENTIEL_MISMATCH_public_cible_${w.row}`),
    ...multiTypeJeu.map(w => `MULTI_VALUE_TYPE_JEU_${w.row}`),
    ...tagResolutions.map(w => `TAG_RESOLUTION_${w.row}`),
    ...distributorItems.map(d => `UNKNOWN_DISTRIBUTOR_${d.row}`),
  ];
  const total = allKeys.length;
  const resolved = allKeys.filter(k => resolutions[k]).length;
  const allResolved = resolved >= total;

  if (total === 0) {
    return (
      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="no-warnings-panel">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <p className="font-medium text-emerald-800">Aucune anomalie à résoudre</p>
        </div>
        <p className="text-xs text-emerald-600 mt-1">Toutes les données sont conformes au référentiel.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="warning-resolution-panel">
      <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <div>
            <p className="font-semibold text-amber-800 text-sm">{total} décision(s) requise(s)</p>
            <p className="text-[10px] text-amber-600">Résolvez chaque anomalie avant de confirmer l'import</p>
          </div>
        </div>
        <Badge variant={allResolved ? "default" : "outline"} className={`text-xs ${allResolved ? 'bg-emerald-600' : ''}`} data-testid="resolution-counter">
          {resolved} / {total}
        </Badge>
      </div>

      {/* ── PUBLISHER_CLOSE_MATCH ── */}
      {publisherCloseMatch.length > 0 && (
        <WarningGroup title={`Éditeurs proches non reconnus (${publisherCloseMatch.length})`} color="orange">
          {publisherCloseMatch.map((w, idx) => {
            const key = `PUBLISHER_CLOSE_MATCH_${w.row}_${w.value}`;
            const r = resolutions[key];
            return (
              <ResolutionCard key={idx} row={w.row} label={w.value} resolved={!!r} testId={`pub-match-${w.row}`} title={getRowTitle(w.row)}>
                <div className="flex flex-wrap gap-1.5">
                  {w.suggestions?.map(s => (
                    <button key={s}
                      onClick={() => onResolve(key, { action: 'use_existing', value: s, row: w.row, field: 'editeur', original: w.value })}
                      className={`px-2.5 py-1 rounded text-xs border transition-all ${
                        r?.action === 'use_existing' && r?.value === s
                          ? 'bg-blue-600 text-white border-blue-600 ring-2 ring-blue-200'
                          : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'
                      }`}
                      data-testid={`resolve-pub-use-${w.row}`}
                    >
                      Réutiliser {'\u00AB'} {s} {'\u00BB'}
                    </button>
                  ))}
                  <button
                    onClick={() => onResolve(key, { action: 'create_new', row: w.row, field: 'editeur', original: w.value })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all ${
                      r?.action === 'create_new'
                        ? 'bg-amber-600 text-white border-amber-600 ring-2 ring-amber-200'
                        : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                    }`}
                    data-testid={`resolve-pub-new-${w.row}`}
                  >
                    Créer {'\u00AB'} {w.value} {'\u00BB'}
                  </button>
                  <button
                    onClick={() => onResolve(key, { action: 'skip', row: w.row, field: 'editeur', original: w.value })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all ${
                      r?.action === 'skip'
                        ? 'bg-gray-600 text-white border-gray-600 ring-2 ring-gray-200'
                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`resolve-pub-skip-${w.row}`}
                  >
                    Ne pas associer
                  </button>
                </div>
              </ResolutionCard>
            );
          })}
        </WarningGroup>
      )}

      {/* ── PUBLISHER_NEW ── */}
      {publisherNew.length > 0 && (
        <WarningGroup title={`Nouveaux éditeurs sans correspondance (${publisherNew.length})`} color="amber">
          {publisherNew.map((w, idx) => {
            const key = `PUBLISHER_NEW_${w.row}_${w.value}`;
            const r = resolutions[key];
            return (
              <ResolutionCard key={idx} row={w.row} label={w.value} resolved={!!r} testId={`pub-new-${w.row}`} title={getRowTitle(w.row)}>
                <p className="text-[10px] text-gray-500 mb-1.5">Aucun éditeur similaire trouvé dans la base.</p>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => onResolve(key, { action: 'create_new', row: w.row, field: 'editeur', original: w.value })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all ${
                      r?.action === 'create_new'
                        ? 'bg-amber-600 text-white border-amber-600 ring-2 ring-amber-200'
                        : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                    }`}
                    data-testid={`resolve-pubnew-confirm-${w.row}`}
                  >
                    Créer {'\u00AB'} {w.value} {'\u00BB'}
                  </button>
                  <button
                    onClick={() => onResolve(key, { action: 'skip', row: w.row, field: 'editeur', original: w.value })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all ${
                      r?.action === 'skip'
                        ? 'bg-gray-600 text-white border-gray-600 ring-2 ring-gray-200'
                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`resolve-pubnew-skip-${w.row}`}
                  >
                    Ne pas associer
                  </button>
                </div>
              </ResolutionCard>
            );
          })}
        </WarningGroup>
      )}

      {/* ── REFERENTIEL_MISMATCH type_jeu ── */}
      {typeJeuMismatch.length > 0 && (
        <WarningGroup title={`Catégorie de jeu non reconnue (${typeJeuMismatch.length})`} color="purple">
          {typeJeuMismatch.map((w, idx) => {
            const key = `REFERENTIEL_MISMATCH_type_jeu_${w.row}`;
            const r = resolutions[key];
            return (
              <ResolutionCard key={idx} row={w.row} label={w.value} resolved={!!r} testId={`tj-mismatch-${w.row}`} title={getRowTitle(w.row)}>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {w.suggestion && (
                    <button
                      onClick={() => onResolve(key, { action: 'use_suggestion', value: w.suggestion, row: w.row, field: 'type_jeu' })}
                      className={`px-2.5 py-1 rounded text-xs border transition-all ${
                        r?.action === 'use_suggestion' && r?.value === w.suggestion
                          ? 'bg-purple-600 text-white border-purple-600 ring-2 ring-purple-200'
                          : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
                      }`}
                      data-testid={`resolve-tj-suggest-${w.row}`}
                    >
                      Utiliser {'\u00AB'} {w.suggestion} {'\u00BB'}
                    </button>
                  )}
                  {refs?.types_jeu?.length > 0 && (
                    <select
                      value={(r?.action === 'use_suggestion' && r?.value !== w.suggestion) ? r.value : ''}
                      onChange={(e) => {
                        if (e.target.value) onResolve(key, { action: 'use_suggestion', value: e.target.value, row: w.row, field: 'type_jeu' });
                      }}
                      className="h-7 px-2 border border-gray-300 rounded text-xs bg-white"
                      data-testid={`resolve-tj-select-${w.row}`}
                    >
                      <option value="">Autre catégorie...</option>
                      {refs.types_jeu.map(tj => (
                        <option key={tj} value={tj}>{tj}</option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={() => onResolve(key, { action: 'skip', row: w.row, field: 'type_jeu' })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all ${
                      r?.action === 'skip'
                        ? 'bg-gray-600 text-white border-gray-600 ring-2 ring-gray-200'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`resolve-tj-skip-${w.row}`}
                  >
                    Ne pas catégoriser
                  </button>
                </div>
              </ResolutionCard>
            );
          })}
        </WarningGroup>
      )}

      {/* ── REFERENTIEL_MISMATCH public_cible ── */}
      {publicCibleMismatch.length > 0 && (
        <WarningGroup title={`Public cible non reconnu (${publicCibleMismatch.length})`} color="slate">
          {publicCibleMismatch.map((w, idx) => {
            const key = `REFERENTIEL_MISMATCH_public_cible_${w.row}`;
            const r = resolutions[key];
            return (
              <ResolutionCard key={idx} row={w.row} label={w.value} resolved={!!r} testId={`pc-mismatch-${w.row}`} title={getRowTitle(w.row)}>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {w.suggestion && (
                    <button
                      onClick={() => onResolve(key, { action: 'use_suggestion', value: w.suggestion, row: w.row, field: 'public_cible' })}
                      className={`px-2.5 py-1 rounded text-xs border transition-all ${
                        r?.action === 'use_suggestion' && r?.value === w.suggestion
                          ? 'bg-slate-600 text-white border-slate-600 ring-2 ring-slate-200'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`resolve-pc-suggest-${w.row}`}
                    >
                      Utiliser {'\u00AB'} {w.suggestion} {'\u00BB'}
                    </button>
                  )}
                  <button
                    onClick={() => onResolve(key, { action: 'skip', row: w.row, field: 'public_cible' })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all ${
                      r?.action === 'skip'
                        ? 'bg-gray-600 text-white border-gray-600 ring-2 ring-gray-200'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`resolve-pc-skip-${w.row}`}
                  >
                    Ne pas renseigner
                  </button>
                </div>
              </ResolutionCard>
            );
          })}
        </WarningGroup>
      )}

      {/* ── MULTI_VALUE_TYPE_JEU ── */}
      {multiTypeJeu.length > 0 && (
        <WarningGroup title={`Multi-catégorie détectée (${multiTypeJeu.length})`} color="indigo">
          {multiTypeJeu.map((w, idx) => {
            const key = `MULTI_VALUE_TYPE_JEU_${w.row}`;
            const r = resolutions[key];
            const allParts = [...(w.resolved || []), ...(w.unresolved || [])];
            return (
              <ResolutionCard key={idx} row={w.row} label={w.value} resolved={!!r} testId={`mtj-${w.row}`} title={getRowTitle(w.row)}>
                <p className="text-[10px] text-gray-500 mb-1.5">Le champ type de jeu n'accepte qu'une seule catégorie. Choisissez celle à retenir :</p>
                <div className="flex flex-wrap gap-1.5">
                  {(w.parts_detail || allParts.map(p => ({ value: p, status: (w.resolved || []).includes(p) ? 'matched' : 'unmatched', suggestion: null }))).map(pd => {
                    const part = pd.canonical || pd.value;
                    const displayName = pd.canonical || pd.value;
                    return (
                      <div key={pd.value} className="flex flex-col gap-0.5">
                        <button
                          onClick={() => onResolve(key, { action: 'select', value: part, row: w.row, field: 'type_jeu' })}
                          className={`px-2.5 py-1 rounded text-xs border transition-all ${
                            r?.action === 'select' && r?.value === part
                              ? 'bg-indigo-600 text-white border-indigo-600 ring-2 ring-indigo-200'
                              : pd.status === 'matched'
                                ? 'bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid={`resolve-mtj-${w.row}-${pd.value}`}
                        >
                          {displayName}
                          {pd.status === 'matched' && <span className="ml-1 text-[9px] opacity-70">(reconnu)</span>}
                        </button>
                        {pd.status === 'unmatched' && pd.suggestion && (
                          <button
                            onClick={() => onResolve(key, { action: 'select', value: pd.suggestion, row: w.row, field: 'type_jeu' })}
                            className={`px-2 py-0.5 rounded text-[10px] border transition-all ${
                              r?.action === 'select' && r?.value === pd.suggestion
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                            }`}
                            data-testid={`resolve-mtj-suggest-${w.row}-${pd.value}`}
                          >
                            Suggestion : {pd.suggestion}
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    onClick={() => onResolve(key, { action: 'skip', row: w.row, field: 'type_jeu' })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all ${
                      r?.action === 'skip' ? 'bg-gray-600 text-white border-gray-600 ring-2 ring-gray-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`resolve-mtj-skip-${w.row}`}
                  >
                    Ne pas catégoriser
                  </button>
                </div>
              </ResolutionCard>
            );
          })}
        </WarningGroup>
      )}

      {/* ── TAG_RESOLUTION ── */}
      {tagResolutions.length > 0 && (
        <WarningGroup title={`Tags produit à résoudre (${tagResolutions.length})`} color="amber">
          {tagResolutions.map((w, idx) => {
            const key = `TAG_RESOLUTION_${w.row}`;
            const r = resolutions[key] || { decisions: {} };
            const detail = w.tags_detail || [];
            // A TAG_RESOLUTION is resolved when every tag has a decision
            const allDecided = detail.every(td => {
              if (td.status === 'matched') {
                const d = r.decisions?.[td.canonical || td.value];
                return d?.decision === 'select' || d?.decision === 'ignore';
              }
              const d = r.decisions?.[td.value];
              return d && (d.decision === 'select' || d.decision === 'create' || d.decision === 'ignore');
            });
            return (
              <ResolutionCard key={idx} row={w.row} label={w.value} resolved={allDecided} testId={`tag-res-${w.row}`} title={getRowTitle(w.row)}>
                <p className="text-[10px] text-gray-500 mb-2">Pour chaque tag, choisissez : associer un existant, créer, ou ignorer.</p>
                <div className="space-y-2">
                  {detail.map((td, ti) => {
                    const tagKey = td.canonical || td.value;
                    const dec = r.decisions?.[tagKey] || r.decisions?.[td.value] || null;
                    const isMatched = td.status === 'matched';
                    const suggInfo = td.suggestion_info;

                    const setDecision = (d) => {
                      const prev = r.decisions || {};
                      onResolve(key, { action: 'tag_resolve', decisions: { ...prev, [tagKey]: d }, row: w.row, field: 'tags_produit' });
                    };

                    return (
                      <div key={ti} className="p-2 bg-gray-50 rounded border border-gray-100" data-testid={`tag-item-${w.row}-${ti}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="font-mono text-xs font-bold">{tagKey}</span>
                          {isMatched && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                              {td.category || 'autre'}
                            </span>
                          )}
                          {!isMatched && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">non trouvé</span>}
                          {dec && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                              dec.decision === 'select' ? 'bg-emerald-100 text-emerald-700' :
                              dec.decision === 'create' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {dec.decision === 'select' ? 'associé' : dec.decision === 'create' ? 'à créer' : 'ignoré'}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {/* Option 1: Associer un tag existant */}
                          {isMatched && (
                            <button
                              onClick={() => setDecision({ decision: 'select', id: td.id })}
                              className={`px-2 py-1 rounded text-xs border transition-all ${
                                dec?.decision === 'select' ? 'bg-emerald-600 text-white border-emerald-600 ring-2 ring-emerald-200' : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                              }`}
                              data-testid={`tag-select-${w.row}-${ti}`}
                            >
                              Associer
                            </button>
                          )}
                          {!isMatched && suggInfo && (
                            <button
                              onClick={() => setDecision({ decision: 'select', id: suggInfo.id, via_suggestion: suggInfo.value })}
                              className={`px-2 py-1 rounded text-xs border transition-all ${
                                dec?.decision === 'select' ? 'bg-purple-600 text-white border-purple-600 ring-2 ring-purple-200' : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
                              }`}
                              data-testid={`tag-suggest-${w.row}-${ti}`}
                            >
                              Utiliser {'\u00AB'} {suggInfo.value} {'\u00BB'}
                              <span className="ml-1 text-[9px] opacity-70">({suggInfo.category})</span>
                            </button>
                          )}
                          {/* Option 2: Créer un nouveau tag */}
                          {!isMatched && (
                            <button
                              onClick={() => setDecision({ decision: 'create', category: dec?.category || 'autre' })}
                              className={`px-2 py-1 rounded text-xs border transition-all ${
                                dec?.decision === 'create' ? 'bg-amber-600 text-white border-amber-600 ring-2 ring-amber-200' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                              }`}
                              data-testid={`tag-create-${w.row}-${ti}`}
                            >
                              Créer
                            </button>
                          )}
                          {/* Category selector (visible only when creating this specific tag) */}
                          {!isMatched && dec?.decision === 'create' && (
                            <select
                              value={dec.category || 'autre'}
                              onChange={(e) => setDecision({ ...dec, category: e.target.value })}
                              className="h-7 px-2 border border-gray-300 rounded text-xs bg-white"
                              data-testid={`tag-category-${w.row}-${ti}`}
                            >
                              {['mecanique', 'theme', 'univers', 'gamme', 'recompense', 'licence', 'autre'].map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          )}
                          {/* Option 3: Ignorer ce tag */}
                          <button
                            onClick={() => setDecision({ decision: 'ignore' })}
                            className={`px-2 py-1 rounded text-xs border transition-all ${
                              dec?.decision === 'ignore' ? 'bg-gray-600 text-white border-gray-600 ring-2 ring-gray-200' : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                            }`}
                            data-testid={`tag-ignore-${w.row}-${ti}`}
                          >
                            Ignorer
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ResolutionCard>
            );
          })}
        </WarningGroup>
      )}

      {/* ── UNKNOWN_DISTRIBUTOR ── */}
      {distributorItems.length > 0 && (
        <WarningGroup title={`Distributeur inconnu (${distributorItems.length})`} color="red">
          {distributorItems.map((d, idx) => {
            const key = `UNKNOWN_DISTRIBUTOR_${d.row}`;
            const r = resolutions[key];
            return (
              <ResolutionCard key={idx} row={d.row} label={d.value || 'inconnu'} resolved={!!r} testId={`dist-unknown-${d.row}`} title={getRowTitle(d.row)}>
                <p className="text-[10px] text-red-600 mb-1.5">
                  Ce distributeur n'existe pas dans la base. Sélectionnez un distributeur existant.
                </p>
                {d.suggestion && (
                  <button
                    onClick={() => onResolve(key, { action: 'use_existing', value: d.suggestion, row: d.row, field: 'distributeur', original: d.value })}
                    className={`px-2.5 py-1 rounded text-xs border transition-all mb-1.5 block ${
                      r?.action === 'use_existing' && r?.value === d.suggestion
                        ? 'bg-blue-600 text-white border-blue-600 ring-2 ring-blue-200'
                        : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'
                    }`}
                    data-testid={`resolve-dist-suggest-${d.row}`}
                  >
                    Suggestion : {'\u00AB'} {d.suggestion} {'\u00BB'}
                  </button>
                )}
                <select
                  value={r?.action === 'use_existing' ? r.value : ''}
                  onChange={(e) => {
                    if (e.target.value) onResolve(key, { action: 'use_existing', value: e.target.value, row: d.row, field: 'distributeur', original: d.value });
                  }}
                  className="w-full h-8 px-2 border border-gray-300 rounded text-xs bg-white"
                  data-testid={`resolve-dist-select-${d.row}`}
                >
                  <option value="">— Sélectionner un distributeur —</option>
                  {partners?.map(p => (
                    <option key={p.id} value={p.nom}>{p.nom}</option>
                  ))}
                </select>
              </ResolutionCard>
            );
          })}
        </WarningGroup>
      )}
    </div>
  );
}

export default function AdminImportV2() {
  const [importType, setImportType] = useState('products');
  const [parsedRows, setParsedRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('upload'); // upload → preview → result
  // P5.3.2: File metadata + auto-detection
  const [fileMeta, setFileMeta] = useState(null);
  const [detection, setDetection] = useState(null);
  const [headerRows, setHeaderRows] = useState(1); // Lignes d'en-tête à ignorer
  
  // Context partner for mechanics import (partner_id injected by backend)
  const [contextPartnerId, setContextPartnerId] = useState('');
  const [partners, setPartners] = useState([]);
  
  // Warning analysis & resolution state
  const [analysisResult, setAnalysisResult] = useState(null);
  const [warningResolutions, setWarningResolutions] = useState({});
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [referentiels, setReferentiels] = useState({ types_jeu: [], publics_cibles: [] });

  // ═══ SessionStorage persistence ═══
  const SESSION_KEY = 'gv_import_v2_session';

  // Restore from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (!saved) return;
      const data = JSON.parse(saved);
      if (data.parsedRows) setParsedRows(data.parsedRows);
      if (data.preview) setPreview(data.preview);
      if (data.fileMeta) setFileMeta(data.fileMeta);
      if (data.detection) setDetection(data.detection);
      if (data.analysisResult) setAnalysisResult(data.analysisResult);
      if (data.warningResolutions) setWarningResolutions(data.warningResolutions);
      if (data.selectedRows) setSelectedRows(new Set(data.selectedRows));
      if (data.step) setStep(data.step);
      if (data.importType) setImportType(data.importType);
      if (data.headerRows) setHeaderRows(data.headerRows);
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save to sessionStorage on meaningful state changes
  useEffect(() => {
    if (!parsedRows && !preview && !analysisResult) {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }
    try {
      const data = {
        parsedRows, preview, fileMeta, detection,
        analysisResult, warningResolutions,
        selectedRows: Array.from(selectedRows),
        step, importType, headerRows,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } catch { /* quota exceeded — ignore */ }
  }, [parsedRows, preview, fileMeta, detection, analysisResult, warningResolutions, selectedRows, step, importType, headerRows]);
  
  // Load partners list for mechanics + products distributor resolution
  useEffect(() => {
    if (importType === 'mechanics' || importType === 'products') {
      api.get('/partners').then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data?.partners || []);
        setPartners(data.sort((a, b) => (a.nom || '').localeCompare(b.nom || '')));
      }).catch(() => {});
    }
  }, [importType]);
  
  // Load referentiels for products import resolution
  useEffect(() => {
    if (importType === 'products') {
      Promise.all([
        api.get('/referentiels/types_jeu').then(r => r.data?.items || r.data || []).catch(() => []),
        api.get('/referentiels/publics_cibles').then(r => r.data?.items || r.data || []).catch(() => []),
      ]).then(([tj, pc]) => {
        const tjArr = Array.isArray(tj) ? tj : [];
        const pcArr = Array.isArray(pc) ? pc : [];
        setReferentiels({
          types_jeu: tjArr.filter(t => t.is_active !== false).map(t => t.value),
          publics_cibles: pcArr.filter(t => t.is_active !== false).map(t => t.value),
        });
      });
    }
  }, [importType]);

  // P5.3.2: Multi-format file upload (CSV/XLSX/JSON)
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    
    if (ext === 'json') {
      // Client-side JSON parse (backward compat)
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target.result);
          const rows = Array.isArray(data) ? data : (data.rows || data.data || [data]);
          setParsedRows(rows);
          setFileMeta({ format: 'json', filename: file.name, file_size_bytes: file.size, columns: rows[0] ? Object.keys(rows[0]) : [], row_count: rows.length });
          setStep('upload');
          setPreview(null);
          setResult(null);
          // Auto-detect
          setDetection(null);
          toast.success(`${rows.length} ligne(s) chargées depuis JSON`);
        } catch {
          toast.error('Format JSON invalide');
        }
      };
      reader.readAsText(file);
    } else if (ext === 'csv' || ext === 'xlsx') {
      // Server-side parse for CSV/XLSX
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post(`/admin/import/v2/upload?header_rows=${headerRows}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setParsedRows(res.data.rows);
        setFileMeta(res.data.metadata);
        setDetection(res.data.detection);
        setStep('upload');
        setPreview(null);
        setResult(null);
        toast.success(`${res.data.row_count} ligne(s) chargées depuis ${ext.toUpperCase()}`);
        // Auto-select detected type if confidence is high
        if (res.data.detection?.confidence >= 0.5) {
          setImportType(res.data.detection.detected_type);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || `Erreur parsing ${ext.toUpperCase()}`);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('Format non supporté. Acceptés: .json, .csv, .xlsx');
    }
  }, [headerRows]);

  const handlePreview = useCallback(async () => {
    if (!parsedRows?.length) return;
    setLoading(true);
    try {
      const res = await api.post(`/admin/import/v2/preview?import_type=${importType}`, parsedRows);
      setPreview(res.data);
      // Initialize selection: all importable rows selected by default, blocked rows deselected
      if (importType === 'products' && res.data.rows) {
        const importable = new Set();
        res.data.rows.forEach(r => {
          if (r.action !== 'blocked' && r.action !== 'skip') {
            importable.add(r.row);
          }
        });
        setSelectedRows(importable);
      }
      setStep('preview');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur preview');
    } finally {
      setLoading(false);
    }
  }, [parsedRows, importType]);

  // ═══ Row selection helpers for products ═══
  const toggleRow = useCallback((rowIdx) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowIdx)) next.delete(rowIdx);
      else next.add(rowIdx);
      return next;
    });
  }, []);

  const selectAllImportable = useCallback(() => {
    if (!preview?.rows) return;
    const importable = new Set();
    preview.rows.forEach(r => {
      if (r.action !== 'blocked' && r.action !== 'skip') importable.add(r.row);
    });
    setSelectedRows(importable);
  }, [preview]);

  const deselectAll = useCallback(() => setSelectedRows(new Set()), []);

  // ═══ Analysis: dry-run to detect warnings requiring resolution ═══
  const analyzeRef = useRef(null);
  const handleAnalyze = useCallback(async () => {
    if (!parsedRows?.length) return;
    setAnalysisLoading(true);
    try {
      const modifiedRows = applyResolutions(parsedRows, warningResolutions);
      const allRowIndices = parsedRows.map((_, i) => i);
      const payload = {
        rows: modifiedRows,
        confirm: false,
        mode: 'upsert',
        filename: fileMeta?.filename || null,
        selected_rows: allRowIndices,
      };
      const res = await api.post('/admin/import/v2/products', payload);
      setAnalysisResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur analyse');
    } finally {
      setAnalysisLoading(false);
    }
  }, [parsedRows, fileMeta, warningResolutions]);

  // ═══ Auto-reanalyze: trigger only when distributor/publisher resolutions change ═══
  const prevResRef = useRef({});
  useEffect(() => {
    if (!analysisResult || !parsedRows?.length) return;
    // Compare current vs previous: only trigger if dist/publisher keys changed
    const prev = prevResRef.current;
    const curr = warningResolutions;
    let needsReanalyze = false;
    for (const k of Object.keys(curr)) {
      if ((k.startsWith('UNKNOWN_DISTRIBUTOR_') || k.startsWith('PUBLISHER_CLOSE_MATCH_') || k.startsWith('PUBLISHER_NEW_')) && curr[k] !== prev[k]) {
        needsReanalyze = true;
        break;
      }
    }
    prevResRef.current = { ...curr };
    if (!needsReanalyze) return;
    if (analyzeRef.current) clearTimeout(analyzeRef.current);
    analyzeRef.current = setTimeout(() => {
      handleAnalyze();
    }, 800);
    return () => { if (analyzeRef.current) clearTimeout(analyzeRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningResolutions]);

  // ═══ Compute unresolved warnings ═══
  const { hasUnresolvedWarnings, analysisNeeded } = useMemo(() => {
    if (importType !== 'products') return { hasUnresolvedWarnings: false, analysisNeeded: false };
    if (!analysisResult) return { hasUnresolvedWarnings: false, analysisNeeded: true };
    const w = analysisResult.warnings || [];
    const e = analysisResult.errors || [];
    const actionableKeys = [
      ...w.filter(x => ['PUBLISHER_CLOSE_MATCH', 'PUBLISHER_NEW'].includes(x.warning_code)).map(x => `${x.warning_code}_${x.row}_${x.value}`),
      ...w.filter(x => x.warning_code === 'REFERENTIEL_MISMATCH').map(x => `REFERENTIEL_MISMATCH_${x.field}_${x.row}`),
      ...w.filter(x => x.warning_code === 'MULTI_VALUE_TYPE_JEU').map(x => `MULTI_VALUE_TYPE_JEU_${x.row}`),
      ...w.filter(x => x.warning_code === 'TAG_RESOLUTION').map(x => `TAG_RESOLUTION_${x.row}`),
      ...e.filter(x => x.error_code === 'UNKNOWN_DISTRIBUTOR' || (typeof x.error === 'string' && x.error.includes('UNKNOWN_DISTRIBUTOR'))).map(x => `UNKNOWN_DISTRIBUTOR_${x.row}`),
    ];
    const unresolved = actionableKeys.some(k => {
      const res = warningResolutions[k];
      if (!res) return true;
      // TAG_RESOLUTION requires every tag to have a decision
      if (k.startsWith('TAG_RESOLUTION_')) {
        const tw = w.find(x => x.warning_code === 'TAG_RESOLUTION' && `TAG_RESOLUTION_${x.row}` === k);
        if (tw) {
          const decisions = res.decisions || {};
          return (tw.tags_detail || []).some(td => {
            const tagKey = td.canonical || td.value;
            const d = decisions[tagKey] || decisions[td.value];
            return !d || !['select', 'create', 'ignore'].includes(d.decision);
          });
        }
      }
      return false;
    });
    return { hasUnresolvedWarnings: unresolved, analysisNeeded: false };
  }, [importType, analysisResult, warningResolutions]);

  // ═══ Effective selection: preview selection + rows importable after resolution ═══
  const effectiveSelectedRows = useMemo(() => {
    const effective = new Set(selectedRows);
    // After analysis with resolutions applied, previously blocked rows may become importable
    if (analysisResult?.rows_detail) {
      for (const rd of analysisResult.rows_detail) {
        if (['create', 'update', 'add_distributor'].includes(rd.action)) {
          effective.add(rd.row);
        }
      }
    }
    return effective;
  }, [selectedRows, analysisResult]);

  const handleConfirmImport = useCallback(async () => {
    if (!parsedRows?.length) return;
    setLoading(true);
    try {
      let res;
      if (importType === 'products') {
        // Apply warning resolutions to rows before sending
        const modifiedRows = applyResolutions(parsedRows, warningResolutions);
        const payload = {
          rows: modifiedRows,
          confirm: true,
          mode: 'upsert',
          filename: fileMeta?.filename || null,
          selected_rows: Array.from(effectiveSelectedRows),
          resolutions: warningResolutions,
        };
        res = await api.post(`/admin/import/v2/${importType}`, payload);
      } else {
        // Offers/mechanics keep the old query-param based flow
        const fnParam = fileMeta?.filename ? `&filename=${encodeURIComponent(fileMeta.filename)}` : '';
        const partnerParam = (importType === 'mechanics' && contextPartnerId) ? `&context_partner_id=${contextPartnerId}` : '';
        res = await api.post(`/admin/import/v2/${importType}?confirm=true${fnParam}${partnerParam}`, parsedRows);
      }
      setResult(res.data);
      setStep('result');
      sessionStorage.removeItem(SESSION_KEY);  // Clear session after successful import
      const created = res.data.created || 0;
      const updated = res.data.updated || 0;
      const addedDist = res.data.added_distributors || 0;
      const skipped = res.data.skipped || 0;
      const errors = res.data.errors?.length || 0;
      const total = created + updated + addedDist;
      if (res.data.empty_selection) {
        toast.error('Aucune ligne sélectionnée pour l\'import.');
      } else if (total === 0 && skipped > 0 && errors === 0) {
        toast.warning('Aucune ligne importée — toutes les lignes ont été ignorées ou désélectionnées.');
      } else if (total === 0 && errors > 0) {
        toast.error(`Import échoué : ${errors} erreur(s) sur ${skipped + errors} ligne(s).`);
      } else if (errors > 0) {
        toast.warning(`Import partiel : ${created} créés, ${updated} mis à jour${addedDist ? `, ${addedDist} distributeurs ajoutés` : ''} — ${errors} erreur(s).`);
      } else {
        toast.success(`Import terminé : ${created} créés, ${updated} mis à jour${addedDist ? `, ${addedDist} distributeurs ajoutés` : ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally {
      setLoading(false);
    }
  }, [parsedRows, importType, fileMeta, effectiveSelectedRows, warningResolutions, contextPartnerId]);

  const reset = () => {
    setParsedRows(null);
    setPreview(null);
    setResult(null);
    setFileMeta(null);
    setDetection(null);
    setStep('upload');
    setAnalysisResult(null);
    setWarningResolutions({});
    sessionStorage.removeItem(SESSION_KEY);
  };

  // P5.3.3: Template info with version
  const [templateInfo, setTemplateInfo] = useState(null);
  
  useEffect(() => {
    api.get('/admin/import/v2/template-info')
      .then(res => setTemplateInfo(res.data))
      .catch(() => {});
  }, []);

  // P5.3.2+P5.3.3: Template download with versioned filename
  const downloadTemplate = useCallback((type, format) => {
    const token = localStorage.getItem('token');
    const url = `${API}/admin/import/v2/templates/${type}?format=${format}`;
    const typeInfo = templateInfo?.types?.find(t => t.type === type);
    const filename = typeInfo ? typeInfo[format] : `template_${type}.${format}`;
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Erreur');
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`${filename} téléchargé`);
      })
      .catch(() => toast.error('Erreur téléchargement template'));
  }, [templateInfo]);

  // P5.2: Commercial Integrity state
  const [auditData, setAuditData] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);

  const runAudit = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/commercial-integrity-audit');
      setAuditData(res.data);
    } catch (err) {
      toast.error('Erreur audit');
    } finally {
      setLoading(false);
    }
  }, []);

  const runMigration = useCallback(async (endpoint, dryRun = true) => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/${endpoint}?dry_run=${dryRun}`);
      setMigrationResult(res.data);
      if (!dryRun) {
        toast.success('Migration appliquée');
        runAudit();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur migration');
    } finally {
      setLoading(false);
    }
  }, [runAudit]);

  // P5.3.1: Import History
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/admin/import/history?limit=30');
      setHistory(res.data);
    } catch (err) {
      toast.error('Erreur chargement historique');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (importType === 'history') {
      loadHistory();
    }
  }, [importType, loadHistory]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-import-v2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Import V2</h1>
            <p className="text-sm text-muted-foreground mt-1">Pipeline industrialisé P5.3.2 — CSV, Excel, JSON</p>
          </div>
          {step !== 'upload' && importType !== 'integrity' && importType !== 'history' && (
            <Button variant="outline" size="sm" onClick={reset} data-testid="import-reset">
              <RotateCcw className="h-4 w-4 mr-2" /> Recommencer
            </Button>
          )}
        </div>

        {/* Type selector */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {IMPORT_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => { setImportType(t.value); reset(); }}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                importType === t.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`import-type-${t.value}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">{t.icon}</span>
                <div>
                  <p className="font-semibold text-sm">{t.label}</p>
                  <p className="text-[10px] text-muted-foreground">{t.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* ═══ PRODUCTS / OFFERS / MECHANICS Import Flow ═══ */}
        {(importType === 'products' || importType === 'offers' || importType === 'mechanics') && (
          <>
            {/* P5.3.2+P5.3.3: Templates download section with version */}
            {step === 'upload' && !parsedRows && (
              <div className="p-4 bg-white border rounded-xl" data-testid="templates-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4 text-primary" />
                    <p className="font-medium text-sm">{"Templates d'import"}</p>
                  </div>
                  {templateInfo?.version && (
                    <Badge variant="outline" className="text-[10px]" data-testid="template-version-badge">
                      v{templateInfo.version}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(templateInfo?.types || [
                    {type: 'products', label: 'Produits'},
                    {type: 'offers', label: 'Offres'},
                    {type: 'mechanics', label: 'Conditions d\'achat'},
                  ]).filter(t => t.type === importType).map(t => (
                    <div key={t.type} className="flex items-center gap-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => downloadTemplate(t.type, 'xlsx')}
                        data-testid={`download-template-${t.type}-xlsx`}
                        className="text-xs"
                      >
                        <FileSpreadsheet className="h-3 w-3 mr-1" />
                        {t.label} .xlsx
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => downloadTemplate(t.type, 'csv')}
                        data-testid={`download-template-${t.type}-csv`}
                        className="text-xs text-muted-foreground"
                      >
                        .csv
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Colonnes libres (commentaires, notes...) autorisées. Ordre des colonnes libre.
                </p>
              </div>
            )}

            {/* Step 1: Upload */}
            {step === 'upload' && (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center space-y-4" data-testid="import-upload-zone">
                
                {/* Context partner selector for mechanics imports */}
                {importType === 'mechanics' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-left mb-4" data-testid="mechanics-partner-selector">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="h-4 w-4 text-blue-600" />
                      <p className="text-sm font-medium text-blue-900">Partenaire cible</p>
                    </div>
                    <p className="text-xs text-blue-700 mb-3">
                      Sélectionnez le partenaire pour lequel vous importez les conditions d'achat. 
                      Le fichier n'a pas besoin de contenir l'identifiant partenaire.
                    </p>
                    <select
                      value={contextPartnerId}
                      onChange={(e) => setContextPartnerId(e.target.value)}
                      className="w-full h-9 px-3 border border-blue-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="context-partner-select"
                    >
                      <option value="">— Sélectionner un partenaire —</option>
                      {partners.map(p => (
                        <option key={p.id} value={p.id}>{p.nom}</option>
                      ))}
                    </select>
                  </div>
                )}

                <Upload className="h-10 w-10 mx-auto text-gray-400" />
                <div>
                  <p className="font-medium">Charger un fichier</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Formats acceptés : <strong>CSV</strong> (;/,/tab), <strong>Excel</strong> (.xlsx), <strong>JSON</strong>
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    <Info className="h-3 w-3 inline mr-1" />
                    Alias français acceptés — le type sera auto-détecté
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-3 text-xs text-muted-foreground">
                    <label htmlFor="headerRows" className="whitespace-nowrap">Lignes d'en-tête à ignorer :</label>
                    <select
                      id="headerRows"
                      value={headerRows}
                      onChange={(e) => setHeaderRows(parseInt(e.target.value))}
                      className="h-7 px-2 border border-gray-300 rounded text-xs bg-white"
                      data-testid="header-rows-select"
                    >
                      <option value={1}>1 (en-tête seul)</option>
                      <option value={2}>2 (en-tête + exemple)</option>
                      <option value={3}>3 (en-tête + libellés + exemple)</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".json,.csv,.xlsx"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="import-file-input"
                    />
                    <Button asChild variant="outline">
                      <span><FileSpreadsheet className="h-4 w-4 mr-2" /> Sélectionner un fichier</span>
                    </Button>
                  </label>
                </div>

                {parsedRows && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg text-left space-y-3">
                    {/* P5.3.2: File metadata */}
                    {fileMeta && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <Badge variant="outline" className="text-xs">{fileMeta.format?.toUpperCase()}</Badge>
                        <Badge variant="secondary" className="text-xs">{parsedRows.length} ligne(s)</Badge>
                        {fileMeta.delimiter && <Badge variant="outline" className="text-[10px]">sep: {fileMeta.delimiter === ';' ? 'point-virgule' : fileMeta.delimiter === ',' ? 'virgule' : 'tab'}</Badge>}
                        {fileMeta.filename && <span className="text-xs text-muted-foreground">{fileMeta.filename}</span>}
                        {fileMeta.header_rows != null && <Badge variant="outline" className="text-[10px]">{fileMeta.header_rows} ligne(s) d'en-tête ignorée(s)</Badge>}
                        {fileMeta.file_size_bytes && <span className="text-[10px] text-muted-foreground">({(fileMeta.file_size_bytes / 1024).toFixed(1)} Ko)</span>}
                      </div>
                    )}

                    {/* P5.3.2+P5.3.3: Auto-detection result with column analysis */}
                    {detection && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="auto-detection">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Table2 className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">
                              Type détecté : <strong>{detection.detected_type === 'products' ? 'Produits' : detection.detected_type === 'offers' ? 'Offres' : 'Conditions d\'achat'}</strong>
                            </span>
                            <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-300">
                              {Math.round(detection.confidence * 100)}%
                            </Badge>
                            {detection.template_version && (
                              <Badge variant="outline" className="text-[10px] text-gray-500 border-gray-300">
                                v{detection.template_version}
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {['products', 'offers', 'mechanics'].filter(t => t !== importType).map(t => (
                              <Button key={t} size="sm" variant="ghost" className="text-xs" onClick={() => setImportType(t)}>
                                {t === 'products' ? 'Produits' : t === 'offers' ? 'Offres' : 'Conditions d\'achat'}
                              </Button>
                            ))}
                          </div>
                        </div>
                        {/* P5.3.3: Column analysis */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px]">
                          {detection.recognized_columns?.length > 0 && (
                            <span className="text-emerald-700">Reconnues : {detection.recognized_columns.length}</span>
                          )}
                          {detection.ignored_columns?.length > 0 && (
                            <span className="text-amber-600" data-testid="detection-ignored-cols">Ignorées : {detection.ignored_columns.join(', ')}</span>
                          )}
                          {detection.aliases_used?.length > 0 && (
                            <span className="text-blue-600">Alias : {detection.aliases_used.length}</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Import en tant que: <strong>{importType === 'products' ? 'Produits' : importType === 'offers' ? 'Offres' : 'Conditions d\'achat'}</strong>
                      </span>
                      <Button size="sm" onClick={handlePreview} disabled={loading} data-testid="import-preview-btn">
                        <Eye className="h-4 w-4 mr-2" /> {loading ? 'Analyse...' : 'Prévisualiser'}
                      </Button>
                    </div>
                    <pre className="text-xs bg-white p-3 rounded border max-h-32 overflow-auto">
                      {JSON.stringify(parsedRows.slice(0, 2), null, 2)}
                      {parsedRows.length > 2 && `\n... et ${parsedRows.length - 2} autres`}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Preview with normalizer data */}
            {step === 'preview' && preview && (
              <div className="space-y-4" data-testid="import-preview">
                {/* P5.3.1: Normalizer Stats */}
                <NormalizerStats stats={preview.normalizer_stats} />

                <div className={`grid gap-3 ${preview.would_add_distributor > 0 ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}>
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                    <p className="text-xl font-bold text-emerald-700">{preview.would_create}</p>
                    <p className="text-[10px] text-emerald-600">Création</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-700">{preview.would_update}</p>
                    <p className="text-[10px] text-blue-600">Mise à jour</p>
                  </div>
                  {preview.would_add_distributor > 0 && (
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-center">
                      <p className="text-xl font-bold text-purple-700">{preview.would_add_distributor}</p>
                      <p className="text-[10px] text-purple-600">Ajout distributeur</p>
                    </div>
                  )}
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                    <p className="text-xl font-bold text-red-700">{preview.would_block || 0}</p>
                    <p className="text-[10px] text-red-600">Bloqué(s)</p>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-xl font-bold text-amber-700">{preview.would_skip}</p>
                    <p className="text-[10px] text-amber-600">Ignoré(s)</p>
                  </div>
                </div>

                {/* P5.3.1: Intra-file duplicates */}
                <IntraDuplicatesPanel duplicates={preview.intra_duplicates} />

                {/* ═══ Product-specific enriched preview table ═══ */}
                {importType === 'products' ? (
                  <ProductPreviewTable
                    rows={preview.rows || []}
                    selectedRows={selectedRows}
                    onToggleRow={toggleRow}
                    onSelectAll={selectAllImportable}
                    onDeselectAll={deselectAll}
                  />
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">#</th>
                            <th className="px-3 py-2 text-left">{importType === 'offers' ? 'offer_code' : importType === 'mechanics' ? 'code_ean' : 'product_id'}</th>
                            <th className="px-3 py-2 text-left">{importType === 'offers' ? 'Titre' : 'Partner'}</th>
                            <th className="px-3 py-2 text-left">Action</th>
                            <th className="px-3 py-2 text-left">Détail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rows?.map((r, idx) => (
                            <tr key={idx} className={r.action === 'skip' ? 'bg-red-50' : r.normalizer_warnings?.length ? 'bg-amber-50/40' : ''}>
                              <td className="px-3 py-1.5 font-mono text-xs">{r.row}</td>
                              <td className="px-3 py-1.5 font-mono text-xs">{r.offer_code || r.code_ean || r.product_id || '-'}</td>
                              <td className="px-3 py-1.5 text-xs max-w-[200px] truncate">{r.intitule || r.title || r.partner_id || '-'}</td>
                              <td className="px-3 py-1.5">
                                <Badge variant={r.action === 'create' ? 'default' : r.action === 'update' ? 'secondary' : 'destructive'} className="text-[10px]">{r.action}</Badge>
                              </td>
                              <td className="px-3 py-1.5 text-xs text-muted-foreground">
                                {r.reason || ''}
                                <CorrectionsBadges corrections={r.corrections} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ═══ Analysis & Warning Resolution (Products only) ═══ */}
                {importType === 'products' && (
                  <div className="space-y-3" data-testid="analysis-section">
                    {!analysisResult ? (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-blue-600" />
                            <div>
                              <p className="font-medium text-blue-800 text-sm">Vérification des données</p>
                              <p className="text-[10px] text-blue-600">
                                Analysez les données pour détecter les anomalies (éditeurs, catégories, distributeurs) avant de confirmer.
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={handleAnalyze}
                            disabled={analysisLoading || !parsedRows?.length}
                            data-testid="analyze-btn"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            {analysisLoading ? 'Analyse...' : 'Lancer l\'analyse'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            Analyse effectuée sur {analysisResult.total} ligne(s)
                            {' '}&mdash;{' '}
                            {analysisResult.created || 0} création(s), {analysisResult.updated || 0} mise(s) à jour, {analysisResult.skipped || 0} ignoré(s)
                          </p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleAnalyze}
                            disabled={analysisLoading}
                            className="text-xs"
                            data-testid="reanalyze-btn"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            {analysisLoading ? 'Analyse...' : 'Réanalyser'}
                          </Button>
                        </div>
                        <WarningResolutionPanel
                          analysisResult={analysisResult}
                          resolutions={warningResolutions}
                          onResolve={(key, val) => setWarningResolutions(prev => ({ ...prev, [key]: val }))}
                          partners={partners.filter(p => p.capabilities?.includes('distributor'))}
                          referentiels={referentiels}
                          parsedRows={parsedRows}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Confirmation */}
                <div className="p-4 bg-gray-50 border rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="h-4 w-4 text-primary" />
                    <p className="font-medium text-sm">Confirmation requise</p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    {"L'import ne sera exécuté qu'après confirmation explicite."}
                    {preview.intra_duplicates?.length > 0 && (
                      <span className="text-amber-600 font-medium"> Attention: {preview.intra_duplicates.length} doublon(s) intra-fichier détecté(s).</span>
                    )}
                    {importType === 'products' && analysisNeeded && (
                      <span className="text-blue-600 font-medium"> Lancez l'analyse des données avant de confirmer.</span>
                    )}
                    {importType === 'products' && !analysisNeeded && hasUnresolvedWarnings && (
                      <span className="text-amber-600 font-medium"> Des anomalies non résolues empêchent la confirmation.</span>
                    )}
                    {importType === 'products' && analysisLoading && (
                      <span className="text-blue-600 font-medium"> Recalcul en cours...</span>
                    )}
                  </p>
                  {/* Recap */}
                  {importType === 'products' && analysisResult && !analysisLoading && (
                    <div className="flex gap-4 mb-3 text-xs">
                      <span className="text-emerald-700">{effectiveSelectedRows.size} produit(s) prêt(s)</span>
                      {(analysisResult.skipped || 0) > 0 && <span className="text-gray-500">{analysisResult.skipped} ignoré(s)</span>}
                      {(analysisResult.errors?.length || 0) > 0 && <span className="text-red-600">{analysisResult.errors.length} bloqué(s)</span>}
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleConfirmImport}
                      disabled={loading || analysisLoading || (importType === 'products' ? (effectiveSelectedRows.size === 0 || analysisNeeded || hasUnresolvedWarnings) : (preview.would_create === 0 && preview.would_update === 0))}
                      data-testid="import-confirm-btn"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      {loading ? 'Import en cours...' : importType === 'products'
                        ? `Importer ${effectiveSelectedRows.size} produit(s)`
                        : `Confirmer l'import (${preview.would_create + preview.would_update} lignes)`
                      }
                    </Button>
                    <Button variant="outline" onClick={reset} data-testid="import-cancel-btn">Annuler</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Result */}
            {step === 'result' && result && (
              <div className="space-y-4" data-testid="import-result">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                  <div>
                    <p className="font-semibold text-emerald-800">Import terminé</p>
                    <p className="text-sm text-emerald-600">
                      {result.created} créé(s) · {result.updated} mis à jour · {result.skipped} ignoré(s) · {result.errors?.length || 0} erreur(s)
                      {result.duration_ms != null && <span className="ml-2 text-emerald-500">({result.duration_ms}ms)</span>}
                    </p>
                  </div>
                </div>

                {/* P5.3.1: Normalizer Stats in result */}
                <NormalizerStats stats={result.normalizer_stats} />

                {/* Intra duplicates in result */}
                <IntraDuplicatesPanel duplicates={result.intra_duplicates} />

                {/* P5.1+P2.4: Warnings — separated by severity */}
                {result.warnings?.length > 0 && (() => {
                  const qualityWarnings = result.warnings.filter(w => 
                    w.message?.includes('MISSING_CODE_EAN') || w.message?.includes('MISSING_DESCRIPTIF')
                  );
                  const otherWarnings = result.warnings.filter(w => 
                    !w.message?.includes('MISSING_CODE_EAN') && !w.message?.includes('MISSING_DESCRIPTIF')
                  );
                  return (
                    <>
                      {/* Structural warnings (ORPHAN, DUPLICATE, etc.) */}
                      {otherWarnings.length > 0 && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="import-warnings">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <p className="font-medium text-amber-800">{otherWarnings.length} avertissement(s)</p>
                          </div>
                          <div className="space-y-1 max-h-32 overflow-auto">
                            {otherWarnings.map((w, idx) => (
                              <p key={idx} className="text-xs text-amber-700">
                                <Badge variant="outline" className="mr-1.5 text-[9px] bg-amber-100">{w.warning_code}</Badge>
                                Ligne {w.row}: {w.message}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Phase 2.4: Quality warnings (EAN, descriptif) */}
                      {qualityWarnings.length > 0 && (
                        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="import-quality-warnings">
                          <div className="flex items-center gap-2 mb-2">
                            <Info className="h-4 w-4 text-orange-500" />
                            <p className="font-medium text-orange-700">
                              {qualityWarnings.length} point(s) qualité à améliorer
                            </p>
                          </div>
                          <div className="space-y-1 max-h-32 overflow-auto">
                            {qualityWarnings.map((w, idx) => (
                              <p key={idx} className="text-xs text-orange-600">
                                <Badge variant="outline" className="mr-1.5 text-[9px] bg-orange-100 border-orange-200 text-orange-700">qualité</Badge>
                                Ligne {w.row}: {w.message.split(': ').slice(1).join(': ') || w.message}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {result.errors?.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <p className="font-medium text-red-800">{result.errors.length} erreur(s)</p>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-auto">
                      {result.errors.map((e, idx) => (
                        <p key={idx} className="text-xs text-red-700 font-mono">
                          Ligne {e.row ?? idx}: {e.error} {e.value ? `(${e.value})` : ''}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Row details with corrections */}
                {result.rows_detail?.length > 0 && result.rows_detail.some(r => r.corrections?.length > 0) && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4 text-blue-600" />
                      <p className="font-medium text-blue-800 text-sm">Corrections automatiques appliquées</p>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-auto">
                      {result.rows_detail.filter(r => r.corrections?.length > 0).map((r, idx) => (
                        <p key={idx} className="text-xs text-blue-700">
                          Ligne {r.row}: {r.corrections.join(', ')}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {result.offer_codes_processed?.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    <FileText className="h-3.5 w-3.5 inline mr-1" />
                    Codes traités: {result.offer_codes_processed.join(', ')}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ═══ INTEGRITY Panel ═══ */}
        {importType === 'integrity' && (
          <div className="space-y-4" data-testid="integrity-panel">
            <div className="flex gap-3">
              <Button onClick={runAudit} disabled={loading} data-testid="run-audit-btn">
                <Eye className="h-4 w-4 mr-2" /> {loading ? 'Audit...' : 'Lancer l\'audit'}
              </Button>
            </div>

            {auditData && (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg border flex items-center gap-3 ${
                  auditData.summary?.status === 'clean' 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  {auditData.summary?.status === 'clean' 
                    ? <CheckCircle className="h-6 w-6 text-emerald-600" />
                    : <AlertTriangle className="h-6 w-6 text-amber-600" />
                  }
                  <div>
                    <p className="font-semibold">{auditData.summary?.status === 'clean' ? 'Système intègre' : 'Actions nécessaires'}</p>
                    <p className="text-sm text-muted-foreground">{auditData.summary?.actions_needed || 0} action(s)</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Produits', val: auditData.products?.total, warn: auditData.products?.without_ean, warnLabel: 'sans EAN' },
                    { label: 'Distributions', val: auditData.distributions?.total, warn: auditData.distributions?.without_product, warnLabel: 'orphelines' },
                    { label: 'Conditions d\'achat', val: auditData.oru?.total, warn: auditData.oru?.orphan_offer_code, warnLabel: 'code orphelin' },
                    { label: 'Offers', val: auditData.offers?.total, warn: auditData.offers?.without_offer_code, warnLabel: 'sans code' },
                  ].map((s, i) => (
                    <div key={i} className="p-3 bg-white border rounded-lg">
                      <p className="text-2xl font-bold">{s.val}</p>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                      {s.warn > 0 && <Badge variant="destructive" className="mt-1 text-[10px]">{s.warn} {s.warnLabel}</Badge>}
                    </div>
                  ))}
                </div>

                {(auditData.offers?.without_offer_code > 0 || auditData.oru?.orphan_offer_code > 0) && (
                  <div className="p-4 bg-white border rounded-lg space-y-3">
                    <p className="font-semibold text-sm">Actions correctives</p>
                    {auditData.offers?.without_offer_code > 0 && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => runMigration('migrate-legacy-offer-codes', true)} disabled={loading}>
                          Prévisualiser migration codes
                        </Button>
                        <Button size="sm" onClick={() => runMigration('migrate-legacy-offer-codes', false)} disabled={loading} data-testid="apply-code-migration">
                          Appliquer
                        </Button>
                      </div>
                    )}
                    {auditData.oru?.orphan_offer_code > 0 && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => runMigration('cleanup-orphan-oru-codes', true)} disabled={loading}>
                          Prévisualiser nettoyage conditions d&apos;achat
                        </Button>
                        <Button size="sm" onClick={() => runMigration('cleanup-orphan-oru-codes', false)} disabled={loading}>
                          Appliquer
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {migrationResult && (
                  <div className="p-4 bg-gray-50 border rounded-lg">
                    <pre className="text-xs max-h-40 overflow-auto">{JSON.stringify(migrationResult, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ HISTORY Panel (P5.3.1) ═══ */}
        {importType === 'history' && (
          <div className="space-y-4" data-testid="import-history-panel">
            <div className="flex items-center gap-3">
              <Button onClick={loadHistory} disabled={historyLoading} variant="outline" size="sm" data-testid="refresh-history-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> {historyLoading ? 'Chargement...' : 'Rafraîchir'}
              </Button>
            </div>

            {history?.history?.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Fichier</th>
                      <th className="px-3 py-2 text-left">Utilisateur</th>
                      <th className="px-3 py-2 text-center">Total</th>
                      <th className="px-3 py-2 text-center">Créés</th>
                      <th className="px-3 py-2 text-center">MàJ</th>
                      <th className="px-3 py-2 text-center">Err</th>
                      <th className="px-3 py-2 text-right">Durée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.history.map((h, idx) => (
                      <tr key={idx} className={idx % 2 ? 'bg-gray-50/50' : ''}>
                        <td className="px-3 py-2 text-xs font-mono">
                          {new Date(h.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={h.type === 'offers' ? 'default' : h.type === 'products' ? 'outline' : 'secondary'} className="text-[10px]">
                            {h.template_type || h.type}
                          </Badge>
                          {h.template_version && (
                            <span className="text-[9px] text-muted-foreground ml-1">v{h.template_version}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground max-w-[120px] truncate" title={h.filename || ''}>
                          {h.filename || <span className="italic text-gray-400">-</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">{h.user_email || '-'}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{h.total_rows}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs text-emerald-600">{h.created}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs text-blue-600">{h.updated}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs text-red-600">{h.errors_count || 0}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{h.duration_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : history ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Aucun import exécuté pour le moment</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
